import { useEffect, useMemo, useState } from "react";
import { Form } from "@/components/Form";
import { TranslationProvider } from "@/contexts/TranslationProvider";
import { Container } from "@/components/ui/Container";
import { Spinner } from "@/components/ui/Spinner";
import { ConfigProvider } from "@/contexts/ConfigProvider";
import classes from "./App.module.scss";
import { getComponentsTranslations } from "@/utils/translations";
import { useService } from "@/hooks/useService";
import type { FormConfig } from "@/types/FormConfig";
import { AppConfigProvider } from "@/contexts/AppConfigProvider";
import { dispatchEvent } from "@/utils/event-dispacher";
import { EventName } from "@/types/Events";
import { useDonationFormStore, useNavigationStore } from "./stores";
import { applyUrlOverrides } from "./utils/configOverrides";
import { ErrorBoundary } from "@sentry/react";
import { ErrorFallback } from "@/components/ErrorFallback";
import { ThemeProvider } from "@/contexts/ThemeProvider";
import { PaymentProvider } from "@/payment/PaymentProvider";
import { ValidationControlProvider } from "@/contexts/ValidationControlProvider";
import { ErrorProcessingProvider } from "@/contexts/ErrorProcessingProvider";
import { ConfigLoadError } from "@/components/ConfigLoadError";
import { captureUtmParameters } from "@/utils/utmParameters";
import {
  opensOnPreviewThankYou,
  previewThankYouResponse,
} from "@/payment/previewThankYou";

function App() {
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { getFormConfig } = useService();
  const init = useDonationFormStore((state) => state.init);

  useEffect(() => {
    getFormConfig()
      .then(async (data) => {
        if (!data) return;

        const searchParams = new URLSearchParams(window.location.search);
        const updatedData = applyUrlOverrides(data, searchParams);
        const utm = captureUtmParameters(searchParams);

        init(updatedData, utm);
        if (opensOnPreviewThankYou(searchParams)) {
          // Before the first render, so the amount step never flashes.
          useNavigationStore
            .getState()
            .completeSubmission(
              await previewThankYouResponse(
                updatedData.formId,
                useDonationFormStore.getState(),
              ),
            );
        }
        setFormConfig(updatedData);
        dispatchEvent(EventName.INIT);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [getFormConfig, init]);

  const translations = useMemo(
    () => getComponentsTranslations(formConfig?.components),
    [formConfig?.components],
  );

  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <div className={classes.pageCenter}>
          <Spinner />
        </div>
      </Container>
    );
  }

  if (error) {
    return <ConfigLoadError />;
  }

  if (!formConfig?.active) return null;

  return (
    <ThemeProvider>
      <ValidationControlProvider enabled>
        <ErrorProcessingProvider>
          <TranslationProvider translations={translations}>
            <AppConfigProvider config={formConfig}>
              <ConfigProvider config={formConfig}>
                <ErrorBoundary fallback={ErrorFallback}>
                  <PaymentProvider>
                    <Form id={formConfig.formId} />
                  </PaymentProvider>
                </ErrorBoundary>
              </ConfigProvider>
            </AppConfigProvider>
          </TranslationProvider>
        </ErrorProcessingProvider>
      </ValidationControlProvider>
    </ThemeProvider>
  );
}

export default App;
